import React from 'react';

type SectionRecord = {
    id: string;
    isDirty: boolean;
    isSaving: boolean;
    save: () => Promise<void>;
};

type SectionSaveCoordinatorValue = {
    registerSection: (record: SectionRecord) => void;
    unregisterSection: (id: string) => void;
    anyDirty: boolean;
    anySaving: boolean;
    saveDirtySections: () => Promise<void>;
};

const SectionSaveCoordinatorContext = React.createContext<SectionSaveCoordinatorValue | null>(null);

export function SectionSaveCoordinatorProvider(props: { children: React.ReactNode }) {
    const recordsRef = React.useRef(new Map<string, SectionRecord>());
    const [, forceRender] = React.useReducer((value) => value + 1, 0);

    const registerSection = React.useCallback((record: SectionRecord) => {
        recordsRef.current.set(record.id, record);
        forceRender();
    }, []);

    const unregisterSection = React.useCallback((id: string) => {
        recordsRef.current.delete(id);
        forceRender();
    }, []);

    const anyDirty = Array.from(recordsRef.current.values()).some((record) => record.isDirty);
    const anySaving = Array.from(recordsRef.current.values()).some((record) => record.isSaving);

    const saveDirtySections = React.useCallback(async () => {
        const dirtyRecords = Array.from(recordsRef.current.values()).filter((record) => record.isDirty);

        for (const record of dirtyRecords) {
            await record.save();
        }
    }, []);

    const value = React.useMemo(
        () => ({
            registerSection,
            unregisterSection,
            anyDirty,
            anySaving,
            saveDirtySections,
        }),
        [anyDirty, anySaving, registerSection, saveDirtySections, unregisterSection]
    );

    return <SectionSaveCoordinatorContext.Provider
        value={value}>{props.children}</SectionSaveCoordinatorContext.Provider>;
}

export function useSectionSaveCoordinator() {
    return React.useContext(SectionSaveCoordinatorContext);
}

export function useRegisterSectionSave(props: {
    id: string;
    isDirty: boolean;
    isSaving: boolean;
    save: () => Promise<void>;
}) {
    const coordinator = useSectionSaveCoordinator();

    React.useEffect(() => {
        if (!coordinator) {
            return;
        }

        coordinator.registerSection({
            id: props.id,
            isDirty: props.isDirty,
            isSaving: props.isSaving,
            save: props.save,
        });

        return () => {
            coordinator.unregisterSection(props.id);
        };
    }, [coordinator, props.id, props.isDirty, props.isSaving, props.save]);
}
